import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { useWebsiteData } from '../../../context/WebsiteDataContext';

const BlogSection = () => {
  const { getPublishedBlogs } = useWebsiteData();
  const blogs = getPublishedBlogs().slice(0, 3); // Show only 3 latest blogs

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (blogs.length === 0) {
    return null;
  }

  return (
    <section id="blog" className="py-16 bg-primary/20 bg-primary">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Latest Insights
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Stay updated with the latest trends, tips, and insights in construction and real estate
          </p>
        </div>

        {/* Blog Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogs.map((blog) => (
            <article key={blog.id} className="relative rounded-lg overflow-hidden backdrop-blur-md bg-white/10 border border-white/20 hover:bg-white/15 transition-colors">
              {/* Blog Image */}
              <div className="relative h-48 overflow-hidden">
                <Image
                  src={blog.featuredImage || '/assets/images/no_image.png'}
                  alt={blog.title}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
                    {blog.category}
                  </span>
                </div>
              </div>

              {/* Blog Content */}
              <div className="p-6">
                {/* Meta Info */}
                <div className="flex items-center space-x-4 text-sm text-white/90 mb-3">
                  <div className="flex items-center space-x-1">
                    <Icon name="User" size={14} />
                    <span>{blog.author}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Calendar" size={14} />
                    <span>{formatDate(blog.date)}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Clock" size={14} />
                    <span>{blog.readTime || '5 min read'}</span>
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-white mb-3 line-clamp-2">
                  {blog.title}
                </h3>

                {/* Excerpt */}
                <p className="text-white/90 mb-4 line-clamp-3">
                  {blog.excerpt}
                </p>

                {/* Tags */}
                {blog.tags && blog.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {blog.tags.slice(0, 3).map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-white/20 text-white text-xs rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Read More */}
                <div className="flex items-center justify-between">
                  <Link
                    to={`/blog/${blog.id}`}
                    className="text-white font-medium hover:text-white/80 transition-colors flex items-center space-x-1"
                  >
                    <span>Read More</span>
                    <Icon name="ArrowRight" size={14} />
                  </Link>
                  
                  <div className="flex items-center space-x-2 text-sm text-white/80">
                    <div className="flex items-center space-x-1">
                      <Icon name="Eye" size={14} />
                      <span>{blog.views || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* View All Blogs Button */}
        <div className="text-center mt-12">
          <Link
            to="/blog"
            className="inline-flex items-center space-x-2 px-6 py-3 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/90 transition-colors font-medium"
          >
            <span>View All Articles</span>
            <Icon name="ArrowRight" size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogSection; 